import classNames from 'classnames';
import {
  memo,
  MouseEventHandler,
  ReactNode,
  Suspense,
  SyntheticEvent,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ArrowBackIcon } from '@/lib/icon';
import { Button, Container, IconSvg } from '@/lib/v2/components';
import { ISelectableItem } from '@/lib/v2/components/Selectable';
import { DownloadIcon } from '@/lib/v2/icons/general/DownloadIcon';
import ModalSelectable from '@/lib/v2/organisms/ModalSelectable';
import { JustifyContentBase } from '@/lib/v2/types';

import { useDownloadContacts } from '@/src/ContactsModule/hooks/useDownloadContacts';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue, useService } from '@/src/infrastructure/Protocol/useEmblue';
import { LIMIT_SELECTION } from '@/src/modules/ContactsModule/constants';

interface SectionHeaderProps {
  icon?: ReactNode;
  title?: ReactNode;
  withoutDivide?: boolean;
  actionsButtons?: ReactNode;
  backButton?: boolean;
  onBack?: MouseEventHandler<HTMLDivElement>;
  justifyContentActionsButtons?: JustifyContentBase;
  children?: ReactNode;
  isLoading?: boolean;
}

const SectionHeader = ({
  icon,
  title,
  withoutDivide = false,
  actionsButtons,
  backButton,
  onBack,
  justifyContentActionsButtons = 'start',
  children,
  isLoading,
}: SectionHeaderProps) => {
  const classes = classNames('flex flex-row gap-3 items-center py-4');
  const { t } = useTranslation();
  const service = useService();
  const classesActionsButtons = classNames('flex flex-1', {
    [`justify-${justifyContentActionsButtons}`]: justifyContentActionsButtons,
  });
  const { groupId } = useParams();

  const [contactsFieldsResponse] = useEmblue(ServiceMethods.getFieldsFilters);

  const contactsFields = useMemo(() => {
    let items: any[] = [
      {
        id: 'email',
        label: 'Email',
        checked: true,
        disabled: true,
      },
    ];
    const data = contactsFieldsResponse?.data;

    if (!data || typeof data === 'string') return [];

    // Defaults fields
    const defaultFields = data?.defaultFields
      ?.filter((item) => item.name !== 'email')
      ?.map((item) => ({
        ...item,
        label: t(`CONTACT_INFORMATION.${item.name}`),
      }));
    items = items.concat(defaultFields ?? []);

    // Customs fields
    const customFields =
      data?.customFields
        ?.filter((item) => item?.metadata?.fieldType !== 'extended')
        ?.map((item) => ({ ...item, label: item.name })) ?? [];
    items = items.concat(customFields);

    // Activity fields
    const activityFields =
      data?.activityFields
        ?.filter((item) => item.name !== 'lastSend')
        ?.map((item) => {
          return {
            ...item,
            id: item.name,
            label: t(`CONTACT_INFORMATION.${item.name}`),
          };
        }) ?? [];
    items = items.concat(activityFields);

    return items;
  }, [contactsFieldsResponse?.data, t]);

  const handleExportContacts = useCallback(
    async (fieldsForExport: ISelectableItem[]) => {
      if (!groupId) return;
      await service?.exportGroupContacts(
        +groupId,
        fieldsForExport.filter((item) => item.id !== 'email')
      );
    },
    [groupId, service]
  );

  const {
    showDownload,
    wordingsDownload,
    closeDownload,
    contactsDownload,
    handleDownload,
    openDownload,
  } = useDownloadContacts({
    onDownload: handleExportContacts,
    filename: 'group',
  });

  const onAcceptDownload = useCallback(
    (e: SyntheticEvent<HTMLButtonElement>, selected: ISelectableItem[]) =>
      void handleDownload(selected),
    [handleDownload]
  );
  return (
    <>
      <Container>
        <div className={classes}>
          {isLoading && (
            <div className="flex w-full animate-pulse flex-col">
              <div className="my-2 h-5 w-1/4 rounded-full bg-gray-200"></div>
            </div>
          )}
          {!isLoading && (
            <>
              <div className="flex flex-row items-center gap-3">
                {backButton && (
                  <IconSvg
                    height="22px"
                    svgComponent={<ArrowBackIcon />}
                    width="22px"
                    onClick={onBack}
                  />
                )}
                <div className="h-full">{icon}</div>
                {title}
              </div>
              <div className={classesActionsButtons}>{actionsButtons}</div>
              {children}
            </>
          )}
          <Button
            outline
            standard
            disabled={contactsDownload.show}
            iconLeft={
              <IconSvg
                fillColor="secondary"
                height="20px"
                svgComponent={<DownloadIcon color="#004DBC" />}
                width="20px"
              />
            }
            id="download-button"
            secondary={true}
            tooltip={t('SEGMENT_DOWNLOAD_MODAL.title')}
            onClick={openDownload}
          >
            {t('SEGMENT_FILTERS.download')}
          </Button>
        </div>
      </Container>
      {!withoutDivide && <div className="h-[1px] w-full bg-[#e5e7eb]"></div>}

      <Suspense>
        <ModalSelectable
          isAcceptDisabled={contactsDownload.disabled}
          items={contactsFields}
          limitSelection={LIMIT_SELECTION}
          show={showDownload}
          wordings={wordingsDownload}
          onAccept={onAcceptDownload}
          onClose={closeDownload}
        />
      </Suspense>
    </>
  );
};

export default memo(SectionHeader);
